"use client"

/**
 * Status picker for the invoice list surfaces (home "My Invoices" + the
 * `/invoices` page).
 *
 * Multi-select — a rep can view Paid **and** Partially Paid in one list, and
 * the home "Paid this week" card's "View all" deep-links to both at once — so
 * it is a checkbox `DropdownMenu` rather than the `Select` the neighbouring
 * single-choice filters use. Pure presentational: the caller owns the
 * selection. An empty selection means "all statuses"; there is no sentinel.
 *
 * The options are the fixed backend-status catalogue (`STATUS_FILTER_OPTIONS`)
 * — unlike `MarketFilter`, whose options come from a live facet feed — so a
 * selection is always a known status and no orphan-seeding is needed.
 */

import { ChevronDown } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { STATUS_FILTER_OPTIONS } from "../filters"
import type { BackendStatus } from "../types"

export interface StatusFilterProps {
  /** The selected statuses; empty means "all statuses". */
  value: BackendStatus[]
  onChange: (value: BackendStatus[]) => void
}

function triggerLabel(value: BackendStatus[]): string {
  if (value.length === 0) return "Status: All"
  if (value.length === 1) {
    const opt = STATUS_FILTER_OPTIONS.find((o) => o.value === value[0])
    return `Status: ${opt?.label ?? value[0]}`
  }
  return `Status: ${value.length} selected`
}

export function StatusFilter({
  value,
  onChange,
}: StatusFilterProps): React.JSX.Element {
  const toggle = (status: BackendStatus) => {
    onChange(
      value.includes(status)
        ? value.filter((selected) => selected !== status)
        : [...value, status],
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-[160px] justify-between font-normal"
          aria-label="Status filter"
        >
          {triggerLabel(value)}
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[180px]">
        {STATUS_FILTER_OPTIONS.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.value}
            checked={value.includes(opt.value)}
            // Keep the menu open so several statuses can be picked in one
            // pass; without this Radix closes on every item selection.
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={() => toggle(opt.value)}
          >
            {opt.label}
          </DropdownMenuCheckboxItem>
        ))}
        {value.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onChange([])}>
              Clear
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
